// 入力フォームの初期値 (initialValues属性にセットする)
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  GetIssuanceDocumentDocument,
  GetIssuanceDocumentListQueryVariables,
  GetIssuanceDocumentQueryVariables,
  IssuanceDocumentInfo,
} from 'views/utils/graphql/graphql';
import { useGetIssuanceDocumentList } from './useGetIssuanceDocumentList';
import { BodyShowList } from 'views/components/molecules/listBody';
import { Client, Context } from 'urql';
import { createBodyShowLists } from '../function/createBodyShowList';
import { DownloadFile, startDownload } from 'views/utils/ui/download';
import { PaginationProps } from 'views/components/atoms/pagination';
import { calcTotalPage } from 'views/utils/calc/pagination';
import { useToast } from 'views/components/atoms/toast/hook';
import { ToastMessageLevel } from 'views/components/atoms/toast';
import { chunk } from 'lodash';
import useOnceSubmit from 'views/utils/useOnceSubmit';

export const useDocumentGrant = () => {
  enum SelectItem {
    Type1 = '取引報告書',
    Type2 = '取引残高報告書',
    Type3 = '受益権原簿記載完了通知',
    Type4 = '信託決算関連書類',
  }
  const documentList: { key: SelectItem; index: number }[] = (
    Object.keys(SelectItem) as (keyof typeof SelectItem)[]
  ).map((key, i) => {
    return { key: SelectItem[key], index: i + 1 };
  });

  const PAGE_COUNT = 10;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedDocumentType, setSelectedDocumentType] = useState<SelectItem>(SelectItem.Type1);
  const getNewDocumentGrantVariables = () => {
    const param: GetIssuanceDocumentListQueryVariables = {
      count: PAGE_COUNT,
      page: currentPage,
      documentType: documentList.find((x) => x.key === selectedDocumentType)?.index,
    };
    return param;
  };

  const { issuanceDocumentList, getIssuanceDocumentList, setIssuanceDocumentListVariables } =
    useGetIssuanceDocumentList(getNewDocumentGrantVariables());
  const [bodyShowLists, setBodyShowLists] = useState<BodyShowList[]>([]);
  const [selectedItems, setSelectedItems] = useState<IssuanceDocumentInfo[]>([]);
  const [canDownload, setCanDownload] = useState<boolean>(false);
  const { toastProps, showToast } = useToast();

  const getNewIssuanceDocumentVariables = (id: string) => {
    const param: GetIssuanceDocumentQueryVariables = {
      id: id,
    };
    return param;
  };

  const useClient = () => useContext(Context);
  const client = useClient() as Client;

  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems([]);
    setIssuanceDocumentListVariables(getNewDocumentGrantVariables());
  }, [selectedDocumentType]);

  useEffect(() => {
    if (!issuanceDocumentList.data) return;
    setBodyShowLists(createBodyShowLists(issuanceDocumentList.data, selectedItems, setSelectedItems));
  }, [issuanceDocumentList, selectedItems]);

  useEffect(() => {
    setCanDownload(selectedItems.length !== 0);
  }, [selectedItems]);

  const getFiles = useCallback(
    async (items: IssuanceDocumentInfo[]): Promise<DownloadFile[]> => {
      // 選択した数が多い場合に並行でHTTPリクエストするのは負荷が高いかもしれないので5ずつにする
      const results = Array<DownloadFile>();
      for (const chunkItems of chunk(selectedItems, 5)) {
        const files = await Promise.all(
          chunkItems.map(async (item) => {
            const param = getNewIssuanceDocumentVariables(item.ID);

            // useだとstate依存で、パラメータをループで変更しても検知できないため、素のclientでコールする
            const res = await client.query(GetIssuanceDocumentDocument, param, { requestPolicy: 'network-only' });
            if (res.data?.GetIssuanceDocument) {
              return { fileName: item.FileName, base64Data: res.data.GetIssuanceDocument.Data };
            } else {
              throw new Error('res.data.GetIssuanceDocument.Data is null');
            }
          }),
        );
        results.push(...files);
      }

      return results;
    },
    [selectedItems],
  );

  const handleDownload = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        if (!canDownload) {
          showToast('ダウンロードしたいファイルを選択してください', ToastMessageLevel.Error);
          return;
        }
        try {
          const files = await getFiles(selectedItems);
          startDownload(files);
        } catch (error) {
          showToast('ファイルのダウンロードに失敗しました', ToastMessageLevel.Error);
        }
      },
    },
    [canDownload, getFiles, selectedItems, startDownload, showToast],
  );

  const selectDocumentType = (value: String) => setSelectedDocumentType(value as SelectItem);

  const paginationData: PaginationProps = {
    currentPage,
    setCurrentPage,
    pageCount: calcTotalPage(
      issuanceDocumentList.data?.GetIssuanceDocumentList?.TotalCount
        ? issuanceDocumentList.data?.GetIssuanceDocumentList?.TotalCount
        : 1,
      PAGE_COUNT,
    ),
    fetchApi: () => setIssuanceDocumentListVariables(getNewDocumentGrantVariables()),
  };

  return {
    documentList,
    selectedDocumentType,
    selectDocumentType,
    bodyShowLists,
    paginationData,
    handleDownload,
    listLoading: issuanceDocumentList.fetching,
    toastProps,
  };
};
